import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DICY from './DICY';
import OracleBetGame from './OracleBet';

const AsymmetricGamesSection = () => {
  const { gameName } = useParams();
  const navigate = useNavigate();
  
  const selectedAsymmetricGame = gameName;

  // Available asymmetric games
  const asymmetricGames = [
    {
      name: 'DICY',
      component: DICY,
      description: 'Bet on outcome of a random variable',
      icon: '🎲',
      features: ['Dice rolling', 'House edge system', 'Investment opportunities', 'ERC20 token shares']
    },
    {
      name: 'OracleBet', 
      component: OracleBetGame,
      description: 'Bet on real-world events using decentralized oracles',
      icon: '🔮',
      features: ['Real-world event betting', 'Oracle-based resolution', 'Sports and news events', 'Decentralized verification']
    }
  ];

  const handleGameSelection = (gameName) => {
    navigate(`/asymmetric/${gameName}`);
  };

  const handleBackToOverview = () => {
    navigate('/asymmetric');
  };

  // If a specific game is selected, render that game component
  if (selectedAsymmetricGame) {
    const selectedGame = asymmetricGames.find(game => game.name === selectedAsymmetricGame);
    if (selectedGame) {
      const GameComponent = selectedGame.component;
      return (
        <div className="space-y-6">
          {/* Back to overview button */}
          <div className="mb-6">
            <button
              onClick={handleBackToOverview}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 transition-colors"
            >
              ← Back to Asymmetric Games Overview
            </button>
          </div>
          
          {/* Render the selected game component */}
          <GameComponent />
        </div>
      );
    }
  }

  // Default overview - show game selection
  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-xl font-semibold text-gray-700 mb-2">Asymmetric Games</h2>
        <p className="text-gray-600">Play against the house or participate in decentralized experiences.</p>
      </div>

      {/* Game Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {asymmetricGames.map((game) => (
          <div
            key={game.name}
            className={`bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-6 cursor-pointer relative ${
              game.name === 'OracleBet' ? 'opacity-75' : ''
            }`}
            onClick={() => handleGameSelection(game.name)}
          >
            {/* Under Construction Banner for OracleBet */}
            {game.name === 'OracleBet' && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                <div className="bg-yellow-500 text-black text-sm font-bold px-6 py-2 rounded-md shadow-lg transform rotate-12 border-2 border-yellow-600">
                  🚧 UNDER CONSTRUCTION 🚧
                </div>
              </div>
            )}
            
            <div className="flex items-start space-x-4">
              <div className="text-3xl">{game.icon}</div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{game.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{game.description}</p>
                
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Features:</h4>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {game.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <span className="text-gray-400 mr-2">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    Click to Play {game.icon}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Information */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200 max-w-3xl mx-auto">
        <h3 className="text-lg font-semibold text-purple-800 mb-3">About Asymmetric Games</h3>
        <p className="text-purple-700 mb-4">
          Unlike 1:1 games, asymmetric games involve different types of participants with varying roles and stakes. 
          Players can compete against automated systems, house edges, or oracle-based outcomes.
        </p>
        <ul className="text-sm text-purple-600 space-y-2">
          <li>• <strong>House Games:</strong> Play against smart contract-based systems</li>
          <li>• <strong>Investment Opportunities:</strong> Become a house investor to earn from player losses</li>
          <li>• <strong>Oracle Integration:</strong> Bet on real-world events with decentralized verification</li>
          <li>• <strong>Flexible Stakes:</strong> Variable betting amounts based on system liquidity</li>
        </ul>
      </div>

      <div className="text-center">
        <p className="text-sm text-gray-500">
          Select a game above to start playing asymmetric games!
        </p>
      </div>
    </div>
  );
};

export default AsymmetricGamesSection;